import React, { useRef } from "react";
import PropTypes from "prop-types";
import { ALL_COLUMNS } from "./ColumnPicker";
import { slaBadgeClass } from "../slaUtils";

const PRIORITY_BADGE = {
  critical: "t2t-badge t2t-badge-critical",
  high:     "t2t-badge t2t-badge-high",
  medium:   "t2t-badge t2t-badge-medium",
  low:      "t2t-badge t2t-badge-low",
};

const STATUS_BADGE = {
  in_progress: "t2t-badge t2t-badge-in_progress",
  approved:    "t2t-badge t2t-badge-approved",
  rejected:    "t2t-badge t2t-badge-rejected",
  on_hold:     "t2t-badge t2t-badge-on_hold",
  draft:       "t2t-badge t2t-badge-draft",
  pending:     "t2t-badge t2t-badge-pending",
  withdrawn:   "t2t-badge t2t-badge-withdrawn",
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function CellValue({ colKey, row }) {
  switch (colKey) {
    case "ref_id":
      return <span className="fw-semibold font-monospace" style={{ color: "var(--t2t-primary)" }}>{row.ref_id}</span>;
    case "title":
      return <span className="fw-medium">{row.title || "—"}</span>;
    case "status":
      return <span className={STATUS_BADGE[row.status] || "t2t-badge t2t-badge-draft"}>{row.status?.replace(/_/g, " ")}</span>;
    case "priority":
      return row.priority
        ? <span className={PRIORITY_BADGE[row.priority] || "t2t-badge t2t-badge-low"}>{row.priority}</span>
        : <span style={{ color: "var(--t2t-text-muted)" }}>—</span>;
    case "sla":
      return row.sla
        ? <span className={slaBadgeClass(row.sla.status)}>{row.sla.label}</span>
        : <span style={{ color: "var(--t2t-text-muted)" }}>—</span>;
    case "created_at":
      return <span className="text-muted small">{formatDate(row.created_at)}</span>;
    case "assignee_name": {
      // BUG-61: fall back to stage role names when no specific user assigned
      if (row.assignee_name) return <span>{row.assignee_name}</span>;
      const roles = Array.isArray(row.stage_role_names) ? row.stage_role_names.filter(Boolean) : [];
      if (roles.length > 0) {
        return (
          <span
            style={{ color: "var(--t2t-text-muted)", fontStyle: "italic" }}
            title={`Anyone with role: ${roles.join(", ")}`}
          >
            Role: {roles.join(", ")}
          </span>
        );
      }
      return <span style={{ color: "var(--t2t-text-muted)" }}>—</span>;
    }
    default:
      return <span className="text-truncate" style={{ maxWidth: 180, display: "inline-block" }}>{row[colKey] || "—"}</span>;
  }
}

CellValue.propTypes = {
  colKey: PropTypes.string.isRequired,
  row:    PropTypes.object.isRequired,
};

export default function TableRow({ row, visibleCols, onClick, isSelected, onCheckboxChange }) {
  const cols = ALL_COLUMNS.filter((c) => visibleCols.includes(c.key));
  const touchX = useRef(null);

  const handleTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 60) onClick(row); // swipe right → open slide-over
    touchX.current = null;
  };

  return (
    <tr
      className={`align-middle ${isSelected ? "table-primary" : "table-hover-row"}`}
      style={{ cursor: "pointer", fontSize: 13 }}
      onClick={() => onClick(row)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Checkbox cell — stop propagation so clicking checkbox doesn't open slide-over */}
      <td style={{ padding: "8px 12px", verticalAlign: "middle", width: 36 }} onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          className="form-check-input"
          checked={isSelected}
          onChange={e => onCheckboxChange(row.request_id, e.target.checked)}
        />
      </td>
      {cols.map((col) => (
        <td key={col.key} style={{ padding: "8px 12px", verticalAlign: "middle" }}>
          <CellValue colKey={col.key} row={row} />
        </td>
      ))}
    </tr>
  );
}

TableRow.propTypes = {
  row:             PropTypes.object.isRequired,
  visibleCols:     PropTypes.arrayOf(PropTypes.string).isRequired,
  onClick:         PropTypes.func.isRequired,
  isSelected:      PropTypes.bool,
  onCheckboxChange: PropTypes.func.isRequired,
};
